import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSignOutMutation, useStopImpersonationMutation } from 'api/main';
import { userSelectors } from 'reducers';
import { parseFullName } from 'utils/helpers';
import { RoleNames, roleCategories, subscriptionStatus } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ConfirmDialog, CustomPopper } from 'components';
import { Box, MenuItem, MenuList } from '@mui/material';
import { useUserRoleMatchCategory } from 'utils/hooks/subscriptions';
import { useSmallScreen } from 'utils/hooks/ui';

const InvestorNavigationSmall = ({
    menuOpen,
    setMenuOpen,
    anchorRef,
    hasSubscriberSignedBS,
    isNewSubscription,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isSmallScreen = useSmallScreen('md');

    const [signOut] = useSignOutMutation();
    const [stopImpersonation, { isLoading: impersonateLoader }] = useStopImpersonationMutation();

    const userIsAdminDistributor = useUserRoleMatchCategory(roleCategories.ADMIN_DISTRIBUTORS);
    const userIsDistributor = useUserRoleMatchCategory(roleCategories.DISTRIBUTORS);
    const userIsInvestor = useUserRoleMatchCategory(roleCategories.INVESTORS);

    const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
    const internalUser = useSelector(userSelectors.getInternalUser);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);

    const isSubscriptionFinished = useMemo(
        () => currentSubscription?.subscriptionStatus === subscriptionStatus.FINISHED,
        [currentSubscription]
    );
    const isAddingIndirectSubscription = useMemo(
        () => location.pathname.includes('add-indirect-subscription'),
        [location]
    );
    const isImpersonatedByDistributor = useMemo(
        () =>
            [RoleNames.ADMIN_CIF, RoleNames.PSI, RoleNames.COMMERCIALISATEUR].includes(
                internalUser?.impersonatorRole
            ),
        [internalUser]
    );

    const handleQuit = useCallback(() => {
        if (isNewSubscription && !isImpersonatedByDistributor) {
            navigate('/dashboard');
            return;
        }
        if (isAddingIndirectSubscription) {
            navigate(-1);
            return;
        }
        if (internalUser?.impersonatedByUser) {
            setConfirmDisconnectOpen(true);
            setMenuOpen(false);
        } else {
            signOut(true);
        }
    }, [
        isNewSubscription,
        isImpersonatedByDistributor,
        isAddingIndirectSubscription,
        internalUser,
        navigate,
        setMenuOpen,
        signOut,
    ]);

    const quitText = useMemo(
        () =>
            internalUser?.impersonatedByUser ? (
                texts.QUITTER
            ) : (
                <React.Fragment>
                    <Box component="span" display="block">
                        {texts.DECONNEXION}
                    </Box>
                    <Box component="span" display="block">
                        {parseFullName(internalUser)}
                    </Box>
                </React.Fragment>
            ),
        [internalUser]
    );

    const handleGoToPath = useCallback(
        targetPath => () => {
            navigate(targetPath);
            setMenuOpen(false);
        },
        [navigate, setMenuOpen]
    );

    function handleListKeyDown(event) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setMenuOpen(false);
        }
    }

    const handleClose = event => {
        if (anchorRef.current?.contains?.(event.target)) {
            return;
        }

        setMenuOpen(false);
    };

    const popupItems = useMemo(
        () => [
            {
                text: texts.ACCUEIL,
                path: '/dashboard',
                shouldDisplay: userIsInvestor && (isSubscriptionFinished || hasSubscriberSignedBS),
            },
            {
                text: texts.MON_COMPTE,
                path: '/my-account',
                shouldDisplay: userIsInvestor && isSubscriptionFinished,
            },
            {
                text: texts.MY_MESSAGES,
                path: '/investor-messages',
                disabled: !isSubscriptionFinished,
                shouldDisplay: userIsInvestor && (isSubscriptionFinished || hasSubscriberSignedBS),
            },
            {
                text: texts.MY_DOCUMENTS,
                path: '/investor-documents',
                shouldDisplay: userIsInvestor && (isSubscriptionFinished || hasSubscriberSignedBS),
            },
            {
                text: texts.MY_PORTFOLIO,
                path: '/portfolio',
                disabled: !isSubscriptionFinished,
                shouldDisplay: userIsInvestor && (isSubscriptionFinished || hasSubscriberSignedBS),
            },
            {
                text: isNewSubscription ? texts.MY_SPACE : quitText,
                action: handleQuit,
                shouldDisplay: true,
            },
        ],
        [
            isSubscriptionFinished,
            userIsInvestor,
            hasSubscriberSignedBS,
            handleQuit,
            isNewSubscription,
            quitText,
        ]
    );

    const handleStopImpersonation = useCallback(() => {
        stopImpersonation();
    }, [stopImpersonation]);

    if (!isSmallScreen) return null;

    return (
        <React.Fragment>
            <CustomPopper open={menuOpen} anchorEl={anchorRef.current} handleClose={handleClose}>
                <MenuList disablePadding autoFocusItem={menuOpen} onKeyDown={handleListKeyDown}>
                    {popupItems.map(el =>
                        el.shouldDisplay ? (
                            <MenuItem
                                key={el.text}
                                disableRipple
                                disabled={el.disabled}
                                sx={{
                                    textTransform: 'uppercase',
                                    justifyContent: 'center',
                                    height: '56px',
                                    width: '180px',
                                    fontSize: '13px',
                                    textAlign: 'center',
                                    whiteSpace: 'break-spaces',
                                }}
                                onClick={el.action ? el.action : handleGoToPath(el.path)}
                            >
                                {el.text}
                            </MenuItem>
                        ) : null
                    )}
                </MenuList>
            </CustomPopper>
            <ConfirmDialog
                loading={impersonateLoader}
                title={
                    userIsAdminDistributor || userIsDistributor
                        ? texts.STOP_IMPERSONATE_DISTRIBUTOR
                        : userIsInvestor
                        ? texts.STOP_IMPERSONATE_SUBSCRIBER
                        : texts.ARRETER_SUBSTITUTION
                }
                open={confirmDisconnectOpen}
                handleClose={() => setConfirmDisconnectOpen(false)}
                handleConfirm={handleStopImpersonation}
            >
                {texts.VOULOIR_DECONNECTER}
            </ConfirmDialog>
        </React.Fragment>
    );
};

InvestorNavigationSmall.propTypes = {
    hasSubscriberSignedBS: PropTypes.bool,
    isNewSubscription: PropTypes.bool,
    menuOpen: PropTypes.bool,
    setMenuOpen: PropTypes.func,
    anchorRef: PropTypes.object,
};

export default InvestorNavigationSmall;
